import qs from 'qs'
import { v4 as uuidv4 } from 'uuid'
import request from 'api/requestUtils'
import type { paths } from '@luxuryescapes/contract-svc-review'
import { mapUserReview } from './mappers/reviewMap'

const BASE_URI = '/api/review'

export type FetchReviewsOkResponse = paths['/api/review']['get']['responses']['200']['content']['application/json']
export type ReviewSummaryType = 'highlight'

type CreateReviewPayload = paths['/api/review']['post']['requestBody']['content']['application/json']
export type CreateReviewOkResponse = paths['/api/review']['post']['responses']['201']['content']['application/json']

type UpdateReviewPayload = paths['/api/review/{reviewId}']['patch']['requestBody']['content']['application/json']
export type UpdateReviewOKResponse = paths['/api/review/{reviewId}']['patch']['responses']['200']['content']['application/json']

type FetchReviewSummaryOkResponse = paths['/api/review/property/{propertyId}/reviewSummary']['get']['responses']['200']['content']['application/json']
type UploadReviewPhotoResponse = paths['/api/review/{orderId}/uploadPhoto']['post']['responses']['201']['content']['application/json']

export type ReviewCompanionshipType = 'alone' | 'partner' | 'friends' | 'family' | 'work'

export interface FetchReviewsParams {
  tourId?: string
  experienceId?: string
  offerId?: string
  /**
   * Load this customer's reviews for one booking. Does not run accommodation mapping;
   * each row keeps the propertyId that was stored at create time.
   */
  orderId?: string
  /**
   * Property-page listing. Combined with `propertyProvider`, svc-review expands related
   * LE/bedbank ids so the same hotel's reviews can appear on either offer page.
   */
  propertyId?: string
  userId?: string
  months?: Array<string>
  ratings?: Array<string>
  companionship?: Array<ReviewCompanionshipType>
  limit?: number
  offset?: number
  sort?: 'newest' | 'oldest' | 'best_rating' | 'worst_rating' | 'most_relevant' | 'recommend'
  useExternalReviews?: boolean
  useBedbankReviews?: boolean
  useHiddenForProperty?: boolean
  includeTranslations?: boolean
  /** Required to interpret `propertyId` as LE vs bedbank when listing a property. Ignored for `orderId` fetches. */
  propertyProvider?: 'bedbank' | 'luxuryescapes'
  search?: string
  showHidden?: boolean
}

export async function fetchReviews(params: FetchReviewsParams): Promise<App.ReviewsFetch> {
  const query = qs.stringify(params)

  const response = await request.get<FetchReviewsOkResponse>(`${BASE_URI}?${query}`)

  return {
    reviews: response.result.map(mapUserReview),
    total: response.total,
  }
}

export async function upsertReview(
  params: App.CreateReviewParams | App.UpdateReviewParams,
): Promise<App.Review> {
  if ('id' in params) {
    const { id, ...toUpdate } = params

    const { result } = await request.patch<UpdateReviewOKResponse, UpdateReviewPayload>(`${BASE_URI}/${id}`, toUpdate, { credentials: 'include' })
    return mapUserReview(result)
  } else {
    const { result } = await request.post<CreateReviewOkResponse, CreateReviewPayload>(BASE_URI, params, { credentials: 'include' })
    return mapUserReview(result)
  }
}

export async function deleteReview(review: App.Review) {
  await request.delete(`${BASE_URI}/${review.id}`, { credentials: 'include' })
}

export async function uploadPhoto(
  file: File,
  orderId: string,
  onProgress?: (percentage: number) => void,
) {
  const data = new FormData()
  data.append('file', file)

  const uri = `${BASE_URI}/${orderId}/uploadPhoto`

  try {
    const { result } = await request.upload<UploadReviewPhotoResponse>(
      {
        path: uri,
        data,
        options: { credentials: 'include' },
      },
      (percentage) => onProgress?.(percentage),
    )
    if (!result.imageId) {
      throw new Error('An error occurred while trying to upload your file.')
    }

    return result
  } catch(error) {
    return Promise.reject(error)
  }
}

export async function deletePhoto(imageId: string, orderId: string) {
  return request.delete(`${BASE_URI}/${orderId}/photo/${imageId}`, { credentials: 'include' })
}

export async function getReviewSummary(
  propertyId: string,
  propertyProvider: string,
  summaryType?: ReviewSummaryType,
  includeTranslations = true,
  maxCharacters?: number,
  refresh = false,
): Promise<App.ReviewSummary> {
  const searchParams = qs.stringify({
    propertyProvider,
    summaryType,
    includeTranslations,
    maxCharacters,
    refresh: refresh ? true : undefined,
    // Bypass responses cached before refresh requests were marked no-store.
    refreshId: refresh ? uuidv4() : undefined,
  })
  const uri = `${BASE_URI}/property/${propertyId}/reviewSummary?${searchParams}`
  return await request.get<FetchReviewSummaryOkResponse>(uri).then(res => res.result)
}
